/**
 * Placement and Geometry Utilities for Design Automation
 * Pure computation functions shared between cloud functions and web app.
 *
 * Includes: geometry primitives, wall helpers, vanity-specific placement,
 * fixture placement, and room utilities.
 */

const { INCHES_TO_METERS, VANITY_DIMENSIONS } = require('./constants');
const { doPolygonsOverlap } = require('../bathroom-analyzer/pipeline/geometry');
const { hydrateRoomOpenings } = require('../openings/openingSync');
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');
const { getWallEndpoints } = require('../collision/wallBoundary');
const { primaryRoomKind } = require('../rooms/roomKind');

/** Gap between mirror back and wall surface for mounting hardware (2mm) */
const MIRROR_MOUNTING_GAP = 0.002;

/**
 * Shortest wall we will derive a direction for (1cm). Below this the
 * endpoint difference is numerical noise and normalizing it yields garbage.
 */
const MIN_WALL_LENGTH = 0.01;

// ─── Geometry Primitives ─────────────────────────────────────────────────────

/**
 * Calculate distance from a point to a line segment
 * @param {Object} point - {x, z} point
 * @param {Object} segStart - {x, z} segment start
 * @param {Object} segEnd - {x, z} segment end
 * @returns {number} distance
 */
function distancePointToSegment(point, segStart, segEnd) {
  const dx = segEnd.x - segStart.x;
  const dz = segEnd.z - segStart.z;
  const lengthSq = dx * dx + dz * dz;

  if (lengthSq === 0) {
    // Segment is a point
    return Math.sqrt((point.x - segStart.x) ** 2 + (point.z - segStart.z) ** 2);
  }

  // Project point onto line, clamped to segment
  let t =
    ((point.x - segStart.x) * dx + (point.z - segStart.z) * dz) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const projX = segStart.x + t * dx;
  const projZ = segStart.z + t * dz;

  return Math.sqrt((point.x - projX) ** 2 + (point.z - projZ) ** 2);
}

/**
 * 2D Ray-segment intersection test in XZ plane
 * @param {Object} rayOrigin - {x, z} origin of the ray
 * @param {Object} rayDir - {x, z} direction of the ray (should be normalized)
 * @param {Object} segStart - {x, z} segment start
 * @param {Object} segEnd - {x, z} segment end
 * @returns {Object|null} { distance, point } or null if no intersection
 */
function raySegmentIntersection2D(rayOrigin, rayDir, segStart, segEnd) {
  const segDx = segEnd.x - segStart.x;
  const segDz = segEnd.z - segStart.z;

  const denom = rayDir.x * segDz - rayDir.z * segDx;

  // Parallel check
  if (Math.abs(denom) < 1e-10) {
    return null;
  }

  const originToSeg = {
    x: segStart.x - rayOrigin.x,
    z: segStart.z - rayOrigin.z,
  };

  // t = distance along ray, u = parameter along segment [0,1]
  const t = (originToSeg.x * segDz - originToSeg.z * segDx) / denom;
  const u = (originToSeg.x * rayDir.z - originToSeg.z * rayDir.x) / denom;

  // Check if intersection is on the ray (t >= 0) and within segment (0 <= u <= 1)
  if (t >= 0 && u >= 0 && u <= 1) {
    return {
      distance: t,
      point: {
        x: rayOrigin.x + t * rayDir.x,
        z: rayOrigin.z + t * rayDir.z,
      },
    };
  }

  return null;
}

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param {Object} point - {x, z} coordinates
 * @param {Array} polygon - Array of {x, z} corners
 * @returns {boolean} true if point is inside polygon
 */
const isPointInPolygon = isPointInsidePolygon;

/**
 * Check if a point is inside a polygon (ray casting algorithm)
 * @param {Object} point - Point {x, z}
 * @param {Array} polygon - Array of polygon corners [{x, z}, ...]
 * @returns {boolean} True if point is inside polygon
 */
function pointInsidePolygon(point, polygon) {
  if (!polygon || polygon.length < 3) return false;
  return isPointInsidePolygon(point, polygon);
}

/**
 * Check if two 2D line segments intersect
 * @param {Object} p1 - First segment start {x, z}
 * @param {Object} p2 - First segment end {x, z}
 * @param {Object} p3 - Second segment start {x, z}
 * @param {Object} p4 - Second segment end {x, z}
 * @returns {boolean} True if segments intersect
 */
function lineSegmentsIntersect(p1, p2, p3, p4) {
  // Calculate direction vectors
  const d1x = p2.x - p1.x;
  const d1z = p2.z - p1.z;
  const d2x = p4.x - p3.x;
  const d2z = p4.z - p3.z;

  // Cross product of direction vectors
  const cross = d1x * d2z - d1z * d2x;

  // If cross product is 0, lines are parallel
  if (Math.abs(cross) < 0.0001) {
    return false;
  }

  // Calculate parameters for intersection point
  const dx = p3.x - p1.x;
  const dz = p3.z - p1.z;

  const t = (dx * d2z - dz * d2x) / cross;
  const u = (dx * d1z - dz * d1x) / cross;

  // Check if intersection is within both segments
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

/**
 * Check if a line segment intersects a polygon
 * @param {Object} lineStart - Line segment start {x, z}
 * @param {Object} lineEnd - Line segment end {x, z}
 * @param {Array} polygon - Array of polygon corners [{x, z}, ...]
 * @returns {boolean} True if line intersects polygon (crosses edges or is inside)
 */
function lineSegmentIntersectsPolygon(lineStart, lineEnd, polygon) {
  if (!polygon || polygon.length < 3) return false;

  // Check if line segment intersects any polygon edge
  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    if (lineSegmentsIntersect(lineStart, lineEnd, polygon[i], polygon[j])) {
      return true;
    }
  }

  // Check if either line endpoint is inside the polygon
  if (pointInsidePolygon(lineStart, polygon)) return true;
  if (pointInsidePolygon(lineEnd, polygon)) return true;

  return false;
}

// ─── Room / Wall Helpers ─────────────────────────────────────────────────────

/**
 * Calculate the true geometric centroid of a polygon using the shoelace formula.
 * This gives the correct center for both convex and concave polygons.
 * @param {Array} corners - Array of {x, z} coordinates
 * @returns {Object} {x, z} centroid coordinates
 */
function calculatePolygonCentroid(corners) {
  if (!corners || corners.length < 3) {
    return null;
  }

  let area = 0;
  let cx = 0;
  let cz = 0;

  for (let i = 0, j = corners.length - 1; i < corners.length; j = i++) {
    const xi = corners[i].x || 0;
    const zi = corners[i].z || 0;
    const xj = corners[j].x || 0;
    const zj = corners[j].z || 0;

    const cross = xj * zi - xi * zj;
    area += cross;
    cx += (xj + xi) * cross;
    cz += (zj + zi) * cross;
  }

  area = area / 2;

  // If area is near zero (degenerate polygon), fall back to simple average
  if (Math.abs(area) < 0.000001) {
    const sumX = corners.reduce((sum, c) => sum + (c.x || 0), 0);
    const sumZ = corners.reduce((sum, c) => sum + (c.z || 0), 0);
    return { x: sumX / corners.length, z: sumZ / corners.length };
  }

  cx = cx / (6 * area);
  cz = cz / (6 * area);

  return { x: cx, z: cz };
}

/**
 * Room kind as a lowercase string, or null when unlabelled. Thin wrapper
 * over the shared resolver so chat tools, web and iOS agree.
 * @param {Object} roomData - The room data object
 * @returns {string|null} The room type label, or null if not found
 */
function getRoomType(roomData) {
  if (!roomData) return null;
  const kind = primaryRoomKind(roomData);
  return kind === 'other' ? null : kind;
}

/**
 * Get the floor surface Y coordinate for a room.
 *
 * Canonical floor-Y helper across the codebase. Web-app's `findFloorY`
 * in `web-app/src/utils/floorPlanUtils.js` delegates here; new web-app
 * code can import this directly via `@shared-lib/vanity-assembly/placementUtils`.
 *
 * Uses `polygonCorners[0].y` (top surface) first, falls back to floor
 * `center.y`. Both should agree but historical processing has produced
 * cases where structure-level and room-level floor Y differ by a few
 * mm; this helper is room-scoped so it always returns the room's own
 * floor.
 *
 * @param {Object} roomData - Room data with floors
 * @param {number} [override] - If provided, returned as-is
 * @returns {number} Floor surface Y coordinate
 */
function getFloorY(roomData, override) {
  if (override !== undefined && override !== null) return override;
  const floor = roomData?.floors?.[0];
  if (!floor) return 0;
  return floor.polygonCorners?.[0]?.y ?? floor.center?.y ?? 0;
}

/**
 * Get the center point of a room from floor polygon corners
 * @param {Object} roomData - Room data with floors
 * @returns {Object} {x, y, z} center position
 */
function getRoomCenter(roomData) {
  const floors = roomData.floors || [];
  if (floors.length === 0 || !floors[0].polygonCorners) {
    return { x: 0, y: 0, z: 0 };
  }

  const corners = floors[0].polygonCorners;
  let sumX = 0,
    sumZ = 0;
  for (const corner of corners) {
    sumX += corner.x || 0;
    sumZ += corner.z || 0;
  }
  return {
    x: sumX / corners.length,
    y: corners[0]?.y || 0,
    z: sumZ / corners.length,
  };
}

/**
 * Get the center point of a wall
 * @param {Object} wall - Wall object
 * @returns {Object} {x, y, z} center position
 */
function getWallCenter(wall) {
  if (wall.alignment?.bottomLeft && wall.alignment?.bottomRight) {
    return {
      x: (wall.alignment.bottomLeft.x + wall.alignment.bottomRight.x) / 2,
      y: wall.alignment.bottomLeft.y || 0,
      z: (wall.alignment.bottomLeft.z + wall.alignment.bottomRight.z) / 2,
    };
  }
  return wall.center || { x: 0, y: 0, z: 0 };
}

/**
 * Get wall height from wall data
 * @param {Object} wall - Wall object
 * @returns {number} Wall height in meters
 */
function getWallHeight(wall) {
  if (wall.dimensions?.y) return wall.dimensions.y;
  if (wall.height) return wall.height;
  // Default wall height if not specified
  return 2.5;
}

/**
 * Get object height from object data
 * @param {Object} obj - Object with dimensions
 * @returns {number} Object height in meters
 */
function getObjectHeight(obj) {
  if (obj.dimensions?.y) return obj.dimensions.y;
  return 0;
}

/**
 * Get the wall's inward normal (pointing into the room)
 * Uses wall.rotation.y which encodes the direction the wall faces
 * @param {Object} wall - Wall object
 * @returns {Object} {x, z} normalized direction vector
 */
/**
 * Build a wall-projection context from a wall's bottom-edge alignment.
 * Returns the unit direction, normal, length, and a `projectOntoWall`
 * closure that maps any XZ point to its signed distance from
 * bottomLeft along the wall.
 *
 * Returns `null` if the wall lacks alignment corners or has zero length.
 *
 * @param {Object} wall - Wall with `alignment.bottomLeft` / `bottomRight`.
 * @returns {{
 *   wallStart: {x:number,z:number},
 *   wallEnd: {x:number,z:number},
 *   wallDir: {x:number,z:number},
 *   wallNormal: {x:number,z:number},
 *   wallLength: number,
 *   projectOntoWall: (point:{x:number,z:number}) => number
 * } | null}
 */
/**
 * A wall's two endpoints, from whichever form the wall stores them in.
 *
 * Most walls carry explicit `alignment.bottomLeft/bottomRight`. Plenty carry
 * only `center` + `dimensions` + `rotation` — a room re-run through Reprocess
 * with `runWallAlignment: false` comes back with every wall in that second
 * form, because the rewrite `batch.set`s room docs from the raw scan with no
 * merge and strips `alignment` off walls that had it. A third set carries
 * bare `start`/`end` (floor-plan edges). Anything reading wall endpoints has
 * to accept all three.
 *
 * The math lives in `getWallEndpoints` (`shared-lib/collision/wallBoundary.js`),
 * which is the one derivation; this adds the degenerate-length guard the
 * vanity callers rely on. `alignment` is not captured data — the pipeline
 * writes it as `calculateWallCorners(wall).bottomLeft/bottomRight`, a pure
 * function of `center`/`dimensions`/`rotation` — so deriving it back is
 * recomputing a cache, not inventing a position. Measured across the 771
 * corpus walls that carry `alignment`: median deviation 0, max 0.6 mm, no
 * start/end order flips.
 *
 * It exists because five call sites had each decided for themselves: two
 * derived a fallback, two threw, and this file's `makeWallProjector` returned
 * null — which made `optimize_vanity` skip its whole accessory pass, so a
 * generated bathroom layout came out with a vanity and no mirror and no
 * vanity light.
 *
 * @returns {{start:{x,z}, end:{x,z}} | null} `null` only when the wall has
 *   none of the three forms, or is degenerately short.
 */
function deriveWallEndpoints(wall) {
  const endpoints = wall ? getWallEndpoints(wall) : null;
  if (!endpoints) return null;
  const dx = endpoints.end.x - endpoints.start.x;
  const dz = endpoints.end.z - endpoints.start.z;
  if (Math.hypot(dx, dz) < MIN_WALL_LENGTH) return null;
  return endpoints;
}

/**
 * Slide a point along its wall so an object of `width` centred there stays
 * inside the wall's own extent, leaving `endGap` at each end.
 *
 * The whole rule in one place because two callers need it and they must not
 * drift: `anchorBareSinkToWall` (G7) and the pedestal/wall-hung realisation
 * in bathroom post-processing. When the object is wider than the wall can
 * hold at all, it is centred on the wall rather than pushed off one end.
 *
 * Pure. Returns a new point; `null` when the wall has no usable direction.
 *
 * @param {Object} wall
 * @param {{x:number, z:number}} point - the position to clamp
 * @param {number} width - the object's along-wall width, metres
 * @param {number} endGap - breathing room at each wall end, metres
 * @returns {{x:number, z:number}|null}
 */
function clampLateralToWallExtent(wall, point, width, endGap) {
  const endpoints = deriveWallEndpoints(wall);
  if (!endpoints || !point) return null;
  const { start: a, end: b } = endpoints;
  const len = Math.hypot(b.x - a.x, b.z - a.z);
  if (len < MIN_WALL_LENGTH) return null;
  const t = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
  const half = (width || 0) / 2;
  const lo = half + endGap;
  const hi = len - half - endGap;
  const along = (point.x - a.x) * t.x + (point.z - a.z) * t.z;
  const clamped = lo > hi ? len / 2 : Math.min(Math.max(along, lo), hi);
  const shift = clamped - along;
  return { x: point.x + t.x * shift, z: point.z + t.z * shift };
}

function makeWallProjector(wall) {
  const endpoints = deriveWallEndpoints(wall);
  if (!endpoints) return null;
  const wallStart = endpoints.start;
  const wallEnd = endpoints.end;
  const dx = wallEnd.x - wallStart.x;
  const dz = wallEnd.z - wallStart.z;
  const wallLength = Math.sqrt(dx * dx + dz * dz);
  if (wallLength === 0) return null;
  const wallDir = { x: dx / wallLength, z: dz / wallLength };
  const wallNormal = getWallInwardNormal(wall);
  const projectOntoWall = (point) => {
    if (!point) return 0;
    return (
      (point.x - wallStart.x) * wallDir.x + (point.z - wallStart.z) * wallDir.z
    );
  };
  return {
    wallStart,
    wallEnd,
    wallDir,
    wallNormal,
    wallLength,
    projectOntoWall,
  };
}

function getWallInwardNormal(wall) {
  // Primary: Use wall rotation (most reliable - encodes wall facing direction)
  if (wall.rotation?.y !== undefined) {
    const ry = wall.rotation.y;
    return { x: Math.sin(ry), z: Math.cos(ry) };
  }

  // Fallback: Calculate from alignment corners
  if (wall.alignment?.bottomLeft && wall.alignment?.bottomRight) {
    const dx = wall.alignment.bottomRight.x - wall.alignment.bottomLeft.x;
    const dz = wall.alignment.bottomRight.z - wall.alignment.bottomLeft.z;
    const length = Math.sqrt(dx * dx + dz * dz);
    if (length > 0.001) {
      return { x: dz / length, z: -dx / length };
    }
  }

  return { x: 0, z: 1 }; // Default facing +Z
}

/**
 * Get the rotation for an object to face into the room (same direction wall faces)
 * @param {Object} wall - Wall object
 * @returns {Object} {x, y, z} rotation in radians
 */
function getWallFacingRotation(wall) {
  if (wall.rotation?.y !== undefined) {
    return { x: 0, y: wall.rotation.y, z: 0 };
  }

  const normal = getWallInwardNormal(wall);
  const ry = Math.atan2(normal.x, normal.z);
  return { x: 0, y: ry, z: 0 };
}

/**
 * Square a fixture to a wall WITHOUT turning it: the multiple of a quarter
 * turn from the wall's facing that is nearest the fixture's own rotation.
 *
 * Seating a scanned fixture is a flush-up, not a re-design. Writing the
 * wall's facing straight onto it cleans up the degree or two of scan noise
 * — and silently rotates by 90 deg any fixture whose back does NOT face
 * that wall, which is every fixture standing END-ON to the wall it touches.
 * 5BC9C86D / "bathroom A" is one: RoomPlan measured a 78.2" x 45.4" tub
 * lying along X with one end flush to a 208.7" wall running along Z, and
 * the seat turned it across the room and moved its centre 42 cm, because
 * the wall spans the box (which is all `wallHostsObject` asks) and nothing
 * asked whether the box faces it.
 *
 * Quantising instead keeps both properties: a fixture already square to the
 * wall gets exactly the old answer, and one a quarter turn off keeps its
 * own orientation, cleaned up to the room's frame.
 *
 * @param {Object} wall
 * @param {number} [rotationY] - the fixture's current rotation; omit to get
 *   the wall's facing, which is what a fixture with no orientation of its
 *   own should take
 * @returns {{rotation: {x,y,z}, quarterTurns: number}} `quarterTurns` counts
 *   the 90 deg steps from the wall's facing: 0 means the fixture's back
 *   faces the wall (its DEPTH is the wall-facing extent), an odd count means
 *   it stands end-on (its WIDTH is).
 */
function squareToWall(wall, rotationY) {
  const facing = getWallFacingRotation(wall);
  const { rotation, quarterTurns } = quarterTurnFrom(facing.y, rotationY);
  return { rotation: { x: 0, y: rotation, z: 0 }, quarterTurns };
}

/**
 * `squareToWall` against a facing angle rather than a wall — for callers
 * that derive the wall's facing themselves (`placeTub` reads it off the
 * endpoints rather than off `wall.rotation`).
 *
 * @param {number} facingY - the wall's inward-facing rotation
 * @param {number} [rotationY] - the fixture's current rotation; omit for the
 *   facing itself
 * @returns {{rotation: number, quarterTurns: number}}
 */
function quarterTurnFrom(facingY, rotationY) {
  if (!Number.isFinite(rotationY))
    return { rotation: facingY, quarterTurns: 0 };
  const QUARTER = Math.PI / 2;
  const steps = Math.round((rotationY - facingY) / QUARTER);
  // A HALF turn is not a turn of the footprint — it is a flip of which face
  // meets the wall, and there the wall wins: a fixture seated against it
  // faces INTO the room, whatever the scan's box says. Only an odd number of
  // quarter turns changes which extent runs along the wall, and that is the
  // one thing the seat must not decide for itself.
  const turns = steps % 2 === 0 ? 0 : steps;
  return {
    rotation: facingY + turns * QUARTER,
    quarterTurns: ((turns % 4) + 4) % 4,
  };
}

/**
 * The extent of a box that faces a wall it has been squared to — its depth
 * when its back is to the wall, its width when it stands end-on. Pair with
 * `squareToWall`, whose `quarterTurns` this reads.
 *
 * @param {{x: number, z: number}} dimensions - the box's LOCAL dimensions
 * @param {number} quarterTurns - from `squareToWall`
 */
function wallFacingExtent(dimensions, quarterTurns) {
  const width = dimensions?.x ?? 0.5;
  const depth = dimensions?.z ?? 0.5;
  return quarterTurns % 2 === 0 ? depth : width;
}

/**
 * Get position for an object placed against a wall, offset by depth
 * @param {Object} wall - Wall object
 * @param {number} depth - Object depth (how far to offset from wall)
 * @returns {Object} {x, y, z} position with object back against wall
 */
function getWallSnappedPosition(wall, depth) {
  const center = getWallCenter(wall);
  const normal = getWallInwardNormal(wall);

  const totalOffset = depth / 2;

  return {
    x: center.x + normal.x * totalOffset,
    y: center.y,
    z: center.z + normal.z * totalOffset,
  };
}

/**
 * Get position for an object on a wall at a specific lateral position.
 * "left" places near bottomLeft, "right" near bottomRight, "center" (default) at wall center.
 * @param {Object} wall - Wall object
 * @param {number} depth - Object depth (how far to offset from wall)
 * @param {string} [wallPosition] - "left", "right", or "center" (default)
 * @param {number} [fixtureWidth] - Fixture width for computing edge margin
 * @returns {Object} {x, y, z} position
 */
function getWallPositionedSnap(
  wall,
  depth,
  wallPosition,
  fixtureWidth,
  options = {}
) {
  if (!wallPosition || wallPosition === 'center') {
    return getWallSnappedPosition(wall, depth);
  }

  const blEndpoints = deriveWallEndpoints(wall);
  const bl = blEndpoints?.start;
  const br = blEndpoints?.end;
  if (!bl || !br) {
    return getWallSnappedPosition(wall, depth);
  }

  const normal = getWallInwardNormal(wall);
  const depthOffset = depth / 2;

  // Wall tangent from left to right
  const dx = br.x - bl.x;
  const dz = br.z - bl.z;
  const wallLength = Math.sqrt(dx * dx + dz * dz);
  if (wallLength < 0.01) {
    return getWallSnappedPosition(wall, depth);
  }
  const tx = dx / wallLength;
  const tz = dz / wallLength;

  // Edge margin: fixture half-width + wall bounds margin (5cm) + 1cm buffer
  // Matches placeToilet's checkWallBounds: halfWidth + WALL_BOUNDS_MARGIN (0.05)
  // For fixtures with side clearance (e.g. toilets need 15" from adjacent walls),
  // minEdgeMargin ensures center is far enough from the wall corner.
  const halfWidth = (fixtureWidth || 0.508) / 2;
  const edgeMargin = Math.max(halfWidth + 0.06, options.minEdgeMargin || 0);

  let basePoint;
  if (wallPosition === 'left') {
    basePoint = {
      x: bl.x + tx * edgeMargin,
      y: bl.y || 0,
      z: bl.z + tz * edgeMargin,
    };
  } else {
    // right
    basePoint = {
      x: br.x - tx * edgeMargin,
      y: br.y || 0,
      z: br.z - tz * edgeMargin,
    };
  }

  return {
    x: basePoint.x + normal.x * depthOffset,
    y: basePoint.y,
    z: basePoint.z + normal.z * depthOffset,
  };
}

/**
 * Re-snap object to wall while preserving its lateral position along the wall.
 * @param {Object} wall - Wall object with bottomCorners
 * @param {Object} currentCenter - Current object center {x, z}
 * @param {number} newDepth - New object depth
 * @returns {Object} {x, z} position with object back against wall, lateral position preserved
 */
function reSnapToWallPreservingLateralPosition(wall, currentCenter, newDepth) {
  const wallCenter = getWallCenter(wall);
  const wallNormal = getWallInwardNormal(wall);

  // Wall tangent (perpendicular to normal, runs along the wall surface)
  const wallTangent = { x: -wallNormal.z, z: wallNormal.x };

  // Calculate lateral offset from wall center (position along the wall)
  const dx = currentCenter.x - wallCenter.x;
  const dz = currentCenter.z - wallCenter.z;
  const lateralDist = dx * wallTangent.x + dz * wallTangent.z;

  // New position: wall center + lateral offset along wall + depth offset perpendicular to wall
  const depthOffset = newDepth / 2;
  return {
    x: wallCenter.x + wallTangent.x * lateralDist + wallNormal.x * depthOffset,
    z: wallCenter.z + wallTangent.z * lateralDist + wallNormal.z * depthOffset,
  };
}

/**
 * Find the wall closest to a cardinal direction (north, south, east, west)
 * @param {Array} walls - Array of wall objects with alignment data
 * @param {Array} floors - Array of floor objects with polygon corners
 * @param {string} direction - 'north', 'south', 'east', 'west'
 * @returns {Object|null} The wall closest to the specified direction
 */
function findWallByDirection(walls, floors, direction) {
  if (!walls || walls.length === 0) return null;

  const directionVectors = {
    north: { x: 0, z: -1 },
    south: { x: 0, z: 1 },
    east: { x: 1, z: 0 },
    west: { x: -1, z: 0 },
  };

  const targetDir = directionVectors[direction.toLowerCase()];
  if (!targetDir) return null;

  let bestWall = null;
  let bestScore = -Infinity;

  for (const wall of walls) {
    if (!wall) continue;

    let wallOutwardNormal = { x: 0, z: -1 };

    if (wall.rotation?.y !== undefined) {
      const ry = wall.rotation.y;
      wallOutwardNormal = { x: -Math.sin(ry), z: -Math.cos(ry) };
    } else if (wall.alignment?.bottomLeft && wall.alignment?.bottomRight) {
      const dx = wall.alignment.bottomRight.x - wall.alignment.bottomLeft.x;
      const dz = wall.alignment.bottomRight.z - wall.alignment.bottomLeft.z;
      const len = Math.sqrt(dx * dx + dz * dz);
      if (len > 0.001) {
        wallOutwardNormal = { x: -dz / len, z: dx / len };
      }
    }

    const score =
      wallOutwardNormal.x * targetDir.x + wallOutwardNormal.z * targetDir.z;

    if (score > bestScore) {
      bestScore = score;
      bestWall = wall;
    }
  }

  return bestScore > 0.5 ? bestWall : null;
}

// ─── Vanity-specific Placement ───────────────────────────────────────────────

/**
 * Resolve placement for vanity based on params
 * @param {Object} params - Parameters with replaceObjectId, wallDirection, or wallId
 * @param {Object} roomData - Room data with objects, walls, floors
 * @returns {Object} { position, rotation, removeId?, wall?, error? }
 */
function resolveVanityPlacement(params, roomData) {
  const { replaceObjectId, wallDirection, wallId } = params;

  // Mode 1: Replace existing object
  if (replaceObjectId) {
    const objects = roomData.objects || [];
    const obj = objects.find((o) => o.id === replaceObjectId);
    if (!obj) {
      return { error: `Object ${replaceObjectId} not found in room` };
    }
    return {
      position: obj.center || { x: 0, y: 0, z: 0 },
      rotation: obj.rotation || { x: 0, y: 0, z: 0 },
      removeId: obj.id,
      parentWallIds: obj.parentWallIds || [],
      dimensions: obj.dimensions,
    };
  }

  // Mode 2: Wall direction
  if (wallDirection) {
    const wall = findWallByDirection(
      roomData.walls || [],
      roomData.floors || [],
      wallDirection
    );
    if (!wall) {
      return {
        error: `No wall found facing ${wallDirection}. Try specifying a wallId directly.`,
      };
    }
    return {
      wall,
      rotation: getWallFacingRotation(wall),
      parentWallIds: [wall.id],
    };
  }

  // Mode 3: Direct wall ID
  if (wallId) {
    const walls = roomData.walls || [];
    const wall = walls.find((w) => w.id === wallId);
    if (!wall) {
      return { error: `Wall ${wallId} not found in room` };
    }
    return {
      wall,
      rotation: getWallFacingRotation(wall),
      parentWallIds: [wall.id],
    };
  }

  return {
    error:
      'No placement specified. Provide replaceObjectId, wallDirection, or wallId.',
  };
}

/**
 * Calculate available wall space on each side of a vanity
 * Uses ray intersection to detect all obstructions including interior walls
 * @param {Object} vanityCenter - {x, z} center position of vanity
 * @param {number} vanityWidth - Width of vanity in meters
 * @param {Object} parentWall - The wall the vanity is placed against
 * @param {Object} roomData - Room data with walls and objects
 * @returns {Object} { leftSpace, rightSpace, leftObstruction, rightObstruction }
 */
function calculateAvailableWallSpace(
  vanityCenter,
  vanityWidth,
  parentWall,
  roomData
) {
  const walls = roomData.walls || [];
  const objects = roomData.objects || [];

  const parentStartEndpoints = deriveWallEndpoints(parentWall);
  const parentStart = parentStartEndpoints?.start;
  const parentEnd = parentStartEndpoints?.end;
  if (!parentStart || !parentEnd) {
    return {
      leftSpace: vanityWidth,
      rightSpace: vanityWidth,
      leftObstruction: { type: 'unknown' },
      rightObstruction: { type: 'unknown' },
    };
  }

  const wallDx = parentEnd.x - parentStart.x;
  const wallDz = parentEnd.z - parentStart.z;
  const wallLength = Math.sqrt(wallDx * wallDx + wallDz * wallDz);
  const wallDir = { x: wallDx / wallLength, z: wallDz / wallLength };

  const halfWidth = vanityWidth / 2;
  const vanityLeftEdge = {
    x: vanityCenter.x - halfWidth * wallDir.x,
    z: vanityCenter.z - halfWidth * wallDir.z,
  };
  const vanityRightEdge = {
    x: vanityCenter.x + halfWidth * wallDir.x,
    z: vanityCenter.z + halfWidth * wallDir.z,
  };

  const projectOntoWall = (point) => {
    const toPoint = { x: point.x - parentStart.x, z: point.z - parentStart.z };
    return toPoint.x * wallDir.x + toPoint.z * wallDir.z;
  };

  const leftEdgeProj = projectOntoWall(vanityLeftEdge);
  const rightEdgeProj = projectOntoWall(vanityRightEdge);

  let leftSpace = leftEdgeProj;
  let rightSpace = wallLength - rightEdgeProj;

  let leftObstruction = { type: 'wallEndpoint' };
  let rightObstruction = { type: 'wallEndpoint' };

  const leftRayDir = { x: -wallDir.x, z: -wallDir.z };
  const rightRayDir = { x: wallDir.x, z: wallDir.z };

  // An object centred within the vanity's own along-wall span is the
  // vanity itself (or something already sitting inside it), not a limit on
  // how far it can grow. Scanned vanity cabinets keep RoomPlan's
  // `category: {storage: {}}` after being retyped to `lower-cabinet`, so
  // they matched the obstruction filter below and the ray from the
  // vanity's edge hit its own cabinet at distance 0 — both sides read 0"
  // and sconces could never fit. Callers don't pass the cabinet id, so
  // the exclusion is geometric.
  const vanityCenterProj = projectOntoWall(vanityCenter);
  const isInsideVanitySpan = (point) =>
    Math.abs(projectOntoWall(point) - vanityCenterProj) < halfWidth;

  // Check all walls for obstructions
  for (const wall of walls) {
    if (wall.id === parentWall.id) continue;

    if (getWallHeight(wall) < 1.0) continue;

    const wallStartEndpoints = deriveWallEndpoints(wall);
    const wallStart = wallStartEndpoints?.start;
    const wallEnd = wallStartEndpoints?.end;
    if (!wallStart || !wallEnd) continue;

    const leftIntersect = raySegmentIntersection2D(
      vanityLeftEdge,
      leftRayDir,
      { x: wallStart.x, z: wallStart.z },
      { x: wallEnd.x, z: wallEnd.z }
    );
    if (leftIntersect && leftIntersect.distance < leftSpace) {
      leftSpace = leftIntersect.distance;
      leftObstruction = { type: 'wall', id: wall.id };
    }

    const rightIntersect = raySegmentIntersection2D(
      vanityRightEdge,
      rightRayDir,
      { x: wallStart.x, z: wallStart.z },
      { x: wallEnd.x, z: wallEnd.z }
    );
    if (rightIntersect && rightIntersect.distance < rightSpace) {
      rightSpace = rightIntersect.distance;
      rightObstruction = { type: 'wall', id: wall.id };
    }
  }

  // Check objects that could obstruct vanity expansion
  const obstructionTypePatterns = [
    'shower',
    'storage',
    'bathtub',
    'toilet',
    'tub',
  ];
  for (const obj of objects) {
    const objType = (obj.type || '').toLowerCase();
    const categoryKeys = obj.category ? Object.keys(obj.category) : [];
    const isRelevantType = obstructionTypePatterns.some(
      (pattern) =>
        objType.includes(pattern) ||
        categoryKeys.some((key) => key.toLowerCase().includes(pattern))
    );
    if (!isRelevantType) continue;
    if (!obj.center || !obj.dimensions) continue;
    if (isInsideVanitySpan(obj.center)) continue;

    const objRotationY = obj.rotation?.y || 0;
    const cosR = Math.abs(Math.cos(objRotationY));
    const sinR = Math.abs(Math.sin(objRotationY));
    const rawHalfW = (obj.dimensions.x || 0.5) / 2;
    const rawHalfD = (obj.dimensions.z || 0.5) / 2;
    const objHalfWidth = rawHalfW * cosR + rawHalfD * sinR;
    const objHalfDepth = rawHalfW * sinR + rawHalfD * cosR;

    const objCenter = obj.center;

    const corners = [
      { x: objCenter.x - objHalfWidth, z: objCenter.z - objHalfDepth },
      { x: objCenter.x + objHalfWidth, z: objCenter.z - objHalfDepth },
      { x: objCenter.x + objHalfWidth, z: objCenter.z + objHalfDepth },
      { x: objCenter.x - objHalfWidth, z: objCenter.z + objHalfDepth },
    ];

    const edges = [
      [corners[0], corners[1]],
      [corners[1], corners[2]],
      [corners[2], corners[3]],
      [corners[3], corners[0]],
    ];

    let foundIntersection = false;
    for (const [edgeStart, edgeEnd] of edges) {
      const leftIntersect = raySegmentIntersection2D(
        vanityLeftEdge,
        leftRayDir,
        edgeStart,
        edgeEnd
      );
      if (leftIntersect && leftIntersect.distance < leftSpace) {
        leftSpace = leftIntersect.distance;
        leftObstruction = { type: obj.type || 'object', id: obj.id };
        foundIntersection = true;
      }

      const rightIntersect = raySegmentIntersection2D(
        vanityRightEdge,
        rightRayDir,
        edgeStart,
        edgeEnd
      );
      if (rightIntersect && rightIntersect.distance < rightSpace) {
        rightSpace = rightIntersect.distance;
        rightObstruction = { type: obj.type || 'object', id: obj.id };
        foundIntersection = true;
      }
    }
  }

  leftSpace = Math.max(0, leftSpace);
  rightSpace = Math.max(0, rightSpace);

  return { leftSpace, rightSpace, leftObstruction, rightObstruction };
}

/**
 * Calculate the maximum allowed vanity depth based on adjacent walls and openings.
 * @param {Object} vanityCenter - Vanity center position {x, z}
 * @param {number} vanityWidth - Vanity width (along parent wall)
 * @param {Object} parentWall - The wall the vanity is mounted against
 * @param {Object} roomData - Room data containing walls, doors, windows, openings
 * @returns {Object} { maxDepth, constraints[] }
 */
function calculateMaxVanityDepth(
  vanityCenter,
  vanityWidth,
  parentWall,
  roomData,
  structureOpenings = null
) {
  // Hydrate slim room copies when canonical structure-level openings are
  // provided — otherwise the opening-overlap loop's `!opening.center` guard
  // silently skips every door / window, leading to vanities placed at full
  // depth even though a door swing path crosses through the cabinet.
  const effectiveRoomData = structureOpenings
    ? hydrateRoomOpenings(roomData, structureOpenings)
    : roomData;
  const walls = effectiveRoomData.walls || [];
  const doors = effectiveRoomData.doors || [];
  const windows = effectiveRoomData.windows || [];
  const openings = effectiveRoomData.openings || [];

  const DEPTH_MARGIN_INCHES = 2;
  const DEPTH_MARGIN = DEPTH_MARGIN_INCHES * INCHES_TO_METERS;

  let maxDepth = 0.9144; // 36 inches default
  const constraints = [];

  const parentStartEndpoints = deriveWallEndpoints(parentWall);
  const parentStart = parentStartEndpoints?.start;
  const parentEnd = parentStartEndpoints?.end;
  if (!parentStart || !parentEnd) {
    return { maxDepth, constraints };
  }

  const wallDx = parentEnd.x - parentStart.x;
  const wallDz = parentEnd.z - parentStart.z;
  const wallLength = Math.sqrt(wallDx * wallDx + wallDz * wallDz);
  const wallDir = { x: wallDx / wallLength, z: wallDz / wallLength };
  const wallNormal = getWallInwardNormal(parentWall);

  const projectOntoWall = (point) => {
    const toPoint = { x: point.x - parentStart.x, z: point.z - parentStart.z };
    return toPoint.x * wallDir.x + toPoint.z * wallDir.z;
  };

  const halfWidth = vanityWidth / 2;
  const vanityLeftProj = projectOntoWall(vanityCenter) - halfWidth;
  const vanityRightProj = projectOntoWall(vanityCenter) + halfWidth;

  // Find adjacent walls (perpendicular walls connected at parent wall endpoints)
  for (const wall of walls) {
    if (wall.id === parentWall.id) continue;

    const wallStartEndpoints = deriveWallEndpoints(wall);
    const wallStart = wallStartEndpoints?.start;
    const wallEnd = wallStartEndpoints?.end;
    if (!wallStart || !wallEnd) continue;

    const otherDx = wallEnd.x - wallStart.x;
    const otherDz = wallEnd.z - wallStart.z;
    const otherLen = Math.sqrt(otherDx * otherDx + otherDz * otherDz);
    if (otherLen < 0.01) continue;
    const otherDir = { x: otherDx / otherLen, z: otherDz / otherLen };

    const dot = Math.abs(wallDir.x * otherDir.x + wallDir.z * otherDir.z);
    if (dot > 0.35) continue;

    const tolerance = 0.05;
    const startsAtParentStart =
      Math.abs(wallStart.x - parentStart.x) < tolerance &&
      Math.abs(wallStart.z - parentStart.z) < tolerance;
    const endsAtParentStart =
      Math.abs(wallEnd.x - parentStart.x) < tolerance &&
      Math.abs(wallEnd.z - parentStart.z) < tolerance;
    const startsAtParentEnd =
      Math.abs(wallStart.x - parentEnd.x) < tolerance &&
      Math.abs(wallStart.z - parentEnd.z) < tolerance;
    const endsAtParentEnd =
      Math.abs(wallEnd.x - parentEnd.x) < tolerance &&
      Math.abs(wallEnd.z - parentEnd.z) < tolerance;

    const connectsToParent =
      startsAtParentStart ||
      endsAtParentStart ||
      startsAtParentEnd ||
      endsAtParentEnd;

    if (!connectsToParent) continue;

    const cornerPoint =
      startsAtParentStart || startsAtParentEnd ? wallStart : wallEnd;
    const farPoint =
      startsAtParentStart || startsAtParentEnd ? wallEnd : wallStart;

    const cornerProj = projectOntoWall(cornerPoint);

    const distFromVanityCenter = Math.abs(
      cornerProj - projectOntoWall(vanityCenter)
    );
    if (distFromVanityCenter > halfWidth + 0.3) continue;

    const farPointPerp =
      (farPoint.x - cornerPoint.x) * wallNormal.x +
      (farPoint.z - cornerPoint.z) * wallNormal.z;

    if (farPointPerp > 0.01) {
      const constrainedDepth = farPointPerp - DEPTH_MARGIN;
      if (constrainedDepth > 0 && constrainedDepth < maxDepth) {
        maxDepth = constrainedDepth;
        constraints.push({
          type: 'adjacentWall',
          wallId: wall.id,
          depth: constrainedDepth,
          position:
            cornerProj < projectOntoWall(vanityCenter) ? 'left' : 'right',
        });
      }
    }
  }

  // Check for openings in adjacent walls
  const allOpenings = [
    ...doors.map((d) => ({ ...d, type: 'door' })),
    ...windows.map((w) => ({ ...w, type: 'window' })),
    ...openings.map((o) => ({ ...o, type: 'opening' })),
  ];

  for (const opening of allOpenings) {
    if (!opening.center || !opening.dimensions) continue;

    const openingParentWallId = opening.parentWallIds?.[0];
    if (!openingParentWallId) continue;
    if (openingParentWallId === parentWall.id) continue;

    const openingWall = walls.find((w) => w.id === openingParentWallId);
    if (!openingWall) continue;

    const owStartEndpoints = deriveWallEndpoints(openingWall);
    const owStart = owStartEndpoints?.start;
    const owEnd = owStartEndpoints?.end;
    if (!owStart || !owEnd) continue;

    const owDx = owEnd.x - owStart.x;
    const owDz = owEnd.z - owStart.z;
    const owLen = Math.sqrt(owDx * owDx + owDz * owDz);
    if (owLen < 0.01) continue;
    const owDir = { x: owDx / owLen, z: owDz / owLen };

    const dot = Math.abs(wallDir.x * owDir.x + wallDir.z * owDir.z);
    if (dot > 0.35) continue;

    const openingPerp =
      (opening.center.x - parentStart.x) * wallNormal.x +
      (opening.center.z - parentStart.z) * wallNormal.z;

    if (openingPerp < 0.01) continue;

    const openingProj = projectOntoWall(opening.center);
    if (
      openingProj < vanityLeftProj - 0.3 ||
      openingProj > vanityRightProj + 0.3
    ) {
      continue;
    }

    const openingHalfDepth = (opening.dimensions.z || opening.dimensions.x) / 2;
    const openingNearEdge = openingPerp - openingHalfDepth - DEPTH_MARGIN;

    if (openingNearEdge > 0.01 && openingNearEdge < maxDepth) {
      maxDepth = openingNearEdge;
      const vanityCenterProj = projectOntoWall(vanityCenter);
      constraints.push({
        type: opening.type,
        id: opening.id,
        depth: openingNearEdge,
        position: openingProj < vanityCenterProj ? 'left' : 'right',
      });
    }
  }

  return { maxDepth, constraints };
}

/**
 * Calculate the front clearance (walking space) in front of a vanity.
 * @param {Object} vanityCenter - Current center position {x, z}
 * @param {number} vanityWidth - Vanity width in meters
 * @param {number} vanityDepth - Vanity depth in meters
 * @param {Object} parentWall - The wall the vanity is attached to
 * @param {Object} roomData - Room data containing walls and objects
 * @param {number} [clearanceCheckWidth] - Optional width to check for clearance
 * @returns {Object} { clearance: number, constraint: string|null }
 */
function calculateFrontClearance(
  vanityCenter,
  vanityWidth,
  vanityDepth,
  parentWall,
  roomData,
  clearanceCheckWidth = null
) {
  const checkWidth =
    clearanceCheckWidth !== null ? clearanceCheckWidth : vanityWidth;
  const walls = roomData.walls || [];
  const objects = roomData.objects || [];

  let clearance = 10;
  let constraint = null;

  const parentStartEndpoints = deriveWallEndpoints(parentWall);
  const parentStart = parentStartEndpoints?.start;
  const parentEnd = parentStartEndpoints?.end;
  if (!parentStart || !parentEnd) {
    return { clearance, constraint };
  }

  const wallNormal = getWallInwardNormal(parentWall);

  const wallDx = parentEnd.x - parentStart.x;
  const wallDz = parentEnd.z - parentStart.z;
  const wallLength = Math.sqrt(wallDx * wallDx + wallDz * wallDz);
  const wallDir = { x: wallDx / wallLength, z: wallDz / wallLength };

  const halfWidth = checkWidth / 2;
  const vanityCenterProj =
    (vanityCenter.x - parentStart.x) * wallDir.x +
    (vanityCenter.z - parentStart.z) * wallDir.z;
  const vanityLeftProj = vanityCenterProj - halfWidth;
  const vanityRightProj = vanityCenterProj + halfWidth;

  // Check distance to walls that are roughly parallel to the parent wall
  for (const wall of walls) {
    if (wall.id === parentWall.id) continue;

    const wallStartEndpoints = deriveWallEndpoints(wall);
    const wallStart = wallStartEndpoints?.start;
    const wallEnd = wallStartEndpoints?.end;
    if (!wallStart || !wallEnd) continue;

    const otherDx = wallEnd.x - wallStart.x;
    const otherDz = wallEnd.z - wallStart.z;
    const otherLen = Math.sqrt(otherDx * otherDx + otherDz * otherDz);
    if (otherLen < 0.01) continue;
    const otherDir = { x: otherDx / otherLen, z: otherDz / otherLen };

    const dot = Math.abs(wallDir.x * otherDir.x + wallDir.z * otherDir.z);
    if (dot < 0.7) continue;

    const wallPerpDist =
      (wallStart.x - parentStart.x) * wallNormal.x +
      (wallStart.z - parentStart.z) * wallNormal.z;

    if (wallPerpDist < vanityDepth / 2 + 0.1) continue;

    const wallStartProj =
      (wallStart.x - parentStart.x) * wallDir.x +
      (wallStart.z - parentStart.z) * wallDir.z;
    const wallEndProj =
      (wallEnd.x - parentStart.x) * wallDir.x +
      (wallEnd.z - parentStart.z) * wallDir.z;
    const wallMinProj = Math.min(wallStartProj, wallEndProj);
    const wallMaxProj = Math.max(wallStartProj, wallEndProj);

    const hasOverlap =
      wallMaxProj > vanityLeftProj && wallMinProj < vanityRightProj;
    if (!hasOverlap) continue;

    const distToWall = wallPerpDist - vanityDepth / 2;

    if (distToWall < clearance && distToWall > 0) {
      clearance = distToWall;
      constraint = `wall ${wall.id}`;
    }
  }

  // Check distance to large objects in front of the vanity
  const obstructingTypes = [
    'toilet',
    'shower',
    'bathtub',
    'tub',
    'storage',
    'washer',
    'dryer',
  ];

  for (const obj of objects) {
    const objType = (obj.type || '').toLowerCase();
    const isObstructing = obstructingTypes.some(
      (t) => objType.includes(t) || obj.category?.[t]
    );
    if (!isObstructing) continue;
    if (!obj.center || !obj.dimensions) continue;

    const objPerpDist =
      (obj.center.x - parentStart.x) * wallNormal.x +
      (obj.center.z - parentStart.z) * wallNormal.z;

    if (objPerpDist < vanityDepth / 2) continue;

    const objHalfDepth = Math.max(obj.dimensions.x, obj.dimensions.z) / 2;
    const distToObj = objPerpDist - objHalfDepth - vanityDepth / 2;

    const objProj =
      (obj.center.x - parentStart.x) * wallDir.x +
      (obj.center.z - parentStart.z) * wallDir.z;
    const objHalfWidth = Math.min(obj.dimensions.x, obj.dimensions.z) / 2;
    const hasOverlap =
      objProj + objHalfWidth > vanityLeftProj &&
      objProj - objHalfWidth < vanityRightProj;

    if (hasOverlap && distToObj < clearance && distToObj > 0) {
      clearance = distToObj;
      constraint = obj.type;
    }
  }

  return { clearance, constraint };
}

/**
 * Check if vanity overlaps with nearby tall objects and calculate required shrinkage.
 * @param {Object} vanityCenter - Center position of the vanity {x, y, z}
 * @param {number} vanityWidth - Width of the vanity in meters
 * @param {number} vanityDepth - Depth of the vanity in meters
 * @param {Object} parentWall - The wall the vanity is against
 * @param {Object} roomData - Room data containing walls, objects
 * @param {string} [vanityId] - ID of the vanity to skip
 * @returns {Object} Collision info
 */
function checkVanityObjectCollision(
  vanityCenter,
  vanityWidth,
  vanityDepth,
  parentWall,
  roomData,
  vanityId = null
) {
  const objects = roomData.objects || [];

  const parentStartEndpoints = deriveWallEndpoints(parentWall);
  const parentStart = parentStartEndpoints?.start;
  const parentEnd = parentStartEndpoints?.end;
  if (!parentStart || !parentEnd) {
    return {
      hasCollision: false,
      reduceLeft: 0,
      reduceRight: 0,
      leftCollider: null,
      rightCollider: null,
    };
  }

  const wallDx = parentEnd.x - parentStart.x;
  const wallDz = parentEnd.z - parentStart.z;
  const wallLength = Math.sqrt(wallDx * wallDx + wallDz * wallDz);
  const wallDir = { x: wallDx / wallLength, z: wallDz / wallLength };
  const wallNormal = { x: -wallDir.z, z: wallDir.x };

  const projectOntoWall = (point) => {
    const toPoint = { x: point.x - parentStart.x, z: point.z - parentStart.z };
    return toPoint.x * wallDir.x + toPoint.z * wallDir.z;
  };

  const projectPerpendicularToWall = (point) => {
    const toPoint = { x: point.x - parentStart.x, z: point.z - parentStart.z };
    return toPoint.x * wallNormal.x + toPoint.z * wallNormal.z;
  };

  const vanityPerpDist = Math.abs(projectPerpendicularToWall(vanityCenter));
  const vanityMaxPerpDist = vanityPerpDist + (vanityDepth || 0.6) / 2;

  const halfWidth = vanityWidth / 2;
  const vanityLeftEdge = {
    x: vanityCenter.x - halfWidth * wallDir.x,
    z: vanityCenter.z - halfWidth * wallDir.z,
  };
  const vanityRightEdge = {
    x: vanityCenter.x + halfWidth * wallDir.x,
    z: vanityCenter.z + halfWidth * wallDir.z,
  };

  const vanityLeftProj = projectOntoWall(vanityLeftEdge);
  const vanityRightProj = projectOntoWall(vanityRightEdge);

  let reduceLeft = 0;
  let reduceRight = 0;
  let leftCollider = null;
  let rightCollider = null;

  // === CHECK WALL OVERLAPS ===
  const walls = roomData.walls || [];
  for (const wall of walls) {
    if (wall.id === parentWall.id) continue;
    if (getWallHeight(wall) < 1.0) continue;

    const wallStartEndpoints = deriveWallEndpoints(wall);
    const wallStart = wallStartEndpoints?.start;
    const wallEnd = wallStartEndpoints?.end;
    if (!wallStart || !wallEnd) continue;

    const wallStartProj = projectOntoWall(wallStart);
    const wallEndProj = projectOntoWall(wallEnd);
    const wallMinProj = Math.min(wallStartProj, wallEndProj);
    const wallMaxProj = Math.max(wallStartProj, wallEndProj);

    const projOverlaps =
      vanityLeftProj < wallMaxProj && vanityRightProj > wallMinProj;
    if (!projOverlaps) continue;

    const wallStartPerp = projectPerpendicularToWall(wallStart);
    const wallEndPerp = projectPerpendicularToWall(wallEnd);
    const wallMinPerp = Math.min(wallStartPerp, wallEndPerp);
    const wallMaxPerp = Math.max(wallStartPerp, wallEndPerp);

    const wallThickness = 0.15;
    const perpOverlaps =
      wallMinPerp - wallThickness <= vanityMaxPerpDist && wallMaxPerp >= 0;

    if (!perpOverlaps) {
      continue;
    }

    const wallProjExtent = wallMaxProj - wallMinProj;
    const wallPerpExtent = wallMaxPerp - wallMinPerp;
    const isPerpendicular =
      wallPerpExtent > wallProjExtent * 2 && wallPerpExtent > 0.3;

    if (isPerpendicular) {
      const wallPosition = (wallMinProj + wallMaxProj) / 2;
      const vanityCenterProj = (vanityLeftProj + vanityRightProj) / 2;
      const vanityIsRightOfWall = vanityCenterProj > wallPosition;

      if (vanityIsRightOfWall && vanityLeftProj < wallPosition) {
        const leftOverlap = wallPosition - vanityLeftProj;
        if (leftOverlap > reduceLeft) {
          reduceLeft = leftOverlap;
          leftCollider = { type: 'wall', id: wall.id };
        }
      }

      if (!vanityIsRightOfWall && vanityRightProj > wallPosition) {
        const rightOverlap = vanityRightProj - wallPosition;
        if (rightOverlap > reduceRight) {
          reduceRight = rightOverlap;
          rightCollider = { type: 'wall', id: wall.id };
        }
      }
    } else {
      if (vanityLeftProj < wallMaxProj && vanityLeftProj > wallMinProj) {
        const leftOverlap = wallMaxProj - vanityLeftProj;
        if (leftOverlap > reduceLeft) {
          reduceLeft = leftOverlap;
          leftCollider = { type: 'wall', id: wall.id };
        }
      }

      if (vanityRightProj > wallMinProj && vanityRightProj < wallMaxProj) {
        const rightOverlap = vanityRightProj - wallMinProj;
        if (rightOverlap > reduceRight) {
          reduceRight = rightOverlap;
          rightCollider = { type: 'wall', id: wall.id };
        }
      }
    }
  }

  // === CHECK OBJECT OVERLAPS ===
  const tallObjectTypePatterns = [
    'shower',
    'storage',
    'bathtub',
    'toilet',
    'tub',
  ];

  for (const obj of objects) {
    if (vanityId && obj.id === vanityId) continue;

    const objType = (obj.type || '').toLowerCase();
    const categoryKeys = obj.category ? Object.keys(obj.category) : [];
    const isRelevantType = tallObjectTypePatterns.some(
      (pattern) =>
        objType.includes(pattern) ||
        categoryKeys.some((key) => key.toLowerCase().includes(pattern))
    );
    if (!isRelevantType) continue;
    if (!obj.center || !obj.dimensions) continue;

    const objRotationY = obj.rotation?.y || 0;
    const cosR = Math.abs(Math.cos(objRotationY));
    const sinR = Math.abs(Math.sin(objRotationY));
    const rawHalfW = (obj.dimensions.x || 0.5) / 2;
    const rawHalfD = (obj.dimensions.z || 0.5) / 2;
    let objHalfWidth = rawHalfW * cosR + rawHalfD * sinR;
    let objHalfDepth = rawHalfW * sinR + rawHalfD * cosR;

    // Toilets need 15" clearance from centerline
    const isToilet =
      objType === 'toilet' || (obj.category && obj.category.toilet);
    if (isToilet) {
      const TOILET_CLEARANCE = 15 * 0.0254; // 15 inches
      objHalfWidth = Math.max(objHalfWidth, TOILET_CLEARANCE);
      objHalfDepth = Math.max(objHalfDepth, TOILET_CLEARANCE);
    }

    const objCenter = obj.center;
    const corners = [
      { x: objCenter.x - objHalfWidth, z: objCenter.z - objHalfDepth },
      { x: objCenter.x + objHalfWidth, z: objCenter.z - objHalfDepth },
      { x: objCenter.x + objHalfWidth, z: objCenter.z + objHalfDepth },
      { x: objCenter.x - objHalfWidth, z: objCenter.z + objHalfDepth },
    ];

    const objPerpDistSigned = projectPerpendicularToWall(objCenter);
    const vanityPerpDistSigned = projectPerpendicularToWall(vanityCenter);
    const objPerpDist = Math.abs(objPerpDistSigned);
    const objMaxDepth = Math.max(objHalfWidth, objHalfDepth);
    const objMinPerpDist = Math.max(0, objPerpDist - objMaxDepth);
    const objMaxPerpDist = objPerpDist + objMaxDepth;

    const sameSide =
      (objPerpDistSigned >= 0 && vanityPerpDistSigned >= 0) ||
      (objPerpDistSigned <= 0 && vanityPerpDistSigned <= 0);

    if (!sameSide) {
      continue;
    }

    const perpOverlaps =
      objMinPerpDist <= vanityMaxPerpDist && objMaxPerpDist >= 0;

    if (!perpOverlaps) {
      continue;
    }

    const projections = corners.map((c) => projectOntoWall(c));
    const objLeftProj = Math.min(...projections);
    const objRightProj = Math.max(...projections);

    const overlaps =
      vanityLeftProj < objRightProj && vanityRightProj > objLeftProj;

    if (overlaps) {
      if (vanityLeftProj < objRightProj && vanityLeftProj > objLeftProj) {
        const leftOverlap = objRightProj - vanityLeftProj;
        if (leftOverlap > reduceLeft) {
          reduceLeft = leftOverlap;
          leftCollider = { type: obj.type || 'object', id: obj.id };
        }
      }

      if (vanityRightProj > objLeftProj && vanityRightProj < objRightProj) {
        const rightOverlap = vanityRightProj - objLeftProj;
        if (rightOverlap > reduceRight) {
          reduceRight = rightOverlap;
          rightCollider = { type: obj.type || 'object', id: obj.id };
        }
      }

      if (objLeftProj >= vanityLeftProj && objRightProj <= vanityRightProj) {
        const objCenterProj = (objLeftProj + objRightProj) / 2;
        const vanityCenterProj = (vanityLeftProj + vanityRightProj) / 2;

        if (objCenterProj < vanityCenterProj) {
          const leftOverlap = objRightProj - vanityLeftProj;
          if (leftOverlap > reduceLeft) {
            reduceLeft = leftOverlap;
            leftCollider = { type: obj.type || 'object', id: obj.id };
          }
        } else {
          const rightOverlap = vanityRightProj - objLeftProj;
          if (rightOverlap > reduceRight) {
            reduceRight = rightOverlap;
            rightCollider = { type: obj.type || 'object', id: obj.id };
          }
        }
      }
    }
  }

  const hasCollision = reduceLeft > 0.001 || reduceRight > 0.001;

  return {
    hasCollision,
    reduceLeft,
    reduceRight,
    leftCollider,
    rightCollider,
  };
}

// ─── Fixture Placement ───────────────────────────────────────────────────────

/**
 * Resolve placement for fixtures (toilet, tub, shower) based on params
 * @param {Object} params - Parameters with replaceObjectId, wallDirection, or wallId
 * @param {Object} roomData - Room data with walls, floors, objects
 * @param {number} fixtureDepth - Depth of the fixture (for wall snapping)
 * @returns {Object} { position, rotation, wall?, parentWallIds?, floorY, removeId?, error? }
 */
function resolveFixturePlacement(
  params,
  roomData,
  fixtureDepth,
  fixtureWidth,
  options
) {
  const { replaceObjectId, wallDirection, wallId, wallPosition } = params;

  const floorY = getFloorY(roomData);

  if (replaceObjectId) {
    const objects = roomData.objects || [];
    const obj = objects.find((o) => o.id === replaceObjectId);
    if (!obj) {
      return { error: `Object ${replaceObjectId} not found in room` };
    }
    return {
      position: obj.center || { x: 0, y: 0, z: 0 },
      rotation: obj.rotation || { x: 0, y: 0, z: 0 },
      removeId: obj.id,
      parentWallIds: obj.parentWallIds || [],
      floorY,
    };
  }

  if (wallDirection) {
    const wall = findWallByDirection(
      roomData.walls || [],
      roomData.floors || [],
      wallDirection
    );
    if (!wall) {
      return {
        error: `No wall found facing ${wallDirection}. Available walls may not align with cardinal directions.`,
      };
    }
    const position = getWallPositionedSnap(
      wall,
      fixtureDepth,
      wallPosition,
      fixtureWidth,
      options
    );
    return {
      position: { x: position.x, z: position.z },
      rotation: getWallFacingRotation(wall),
      wall,
      parentWallIds: [wall.id],
      floorY,
    };
  }

  if (wallId) {
    const walls = roomData.walls || [];
    const wall = walls.find((w) => w.id === wallId);
    if (!wall) {
      return { error: `Wall ${wallId} not found in room` };
    }
    const position = getWallPositionedSnap(
      wall,
      fixtureDepth,
      wallPosition,
      fixtureWidth,
      options
    );
    return {
      position: { x: position.x, z: position.z },
      rotation: getWallFacingRotation(wall),
      wall,
      parentWallIds: [wall.id],
      floorY,
    };
  }

  return {
    error:
      'No placement specified. Provide replaceObjectId, wallDirection, or wallId.',
  };
}

/**
 * Compute per-side countertop overhangs with suppression using SAT overlap checks.
 * Builds thin overhang strips for each side and checks if they overlap any
 * nearby fixture or wall. Suppresses overhang on sides that collide.
 *
 * @param {Object} opts
 * @param {number} opts.cabinetWidth - Cabinet width along wall tangent (meters)
 * @param {number} opts.cabinetDepth - Cabinet depth into room (meters)
 * @param {Object} opts.cabinetCenter - Cabinet center {x, z}
 * @param {Object} opts.parentWall - Parent wall object
 * @param {Object} [opts.roomData] - Room data {walls, objects, doors, windows}
 * @param {Object} [opts.spaceInfo] - Pre-computed (unused, kept for API compat)
 * @returns {{ leftOverhang, rightOverhang, frontOverhang, countertopWidth, countertopDepth, centerOffset }}
 */
function computeCountertopOverhangs({
  cabinetWidth,
  cabinetDepth,
  cabinetCenter,
  parentWall,
  roomData,
  frontOverhangOverride,
}) {
  const overhang = VANITY_DIMENSIONS.countertop.overhang;
  // The SIDES keep the nosing; the FRONT may be asked for more, which is how
  // a counter carries a basin over a carcass shallower than the basin needs.
  // Obstruction suppression below applies to whichever figure is in force.
  const front = Number.isFinite(frontOverhangOverride)
    ? frontOverhangOverride
    : overhang;

  let leftOverhang = overhang;
  let rightOverhang = overhang;
  let frontOverhang = front;

  if (parentWall && roomData) {
    const wallNormal = getWallInwardNormal(parentWall);
    const wallTangent = { x: -wallNormal.z, z: wallNormal.x };
    const halfW = cabinetWidth / 2;
    const halfD = cabinetDepth / 2;
    const cx = cabinetCenter.x;
    const cz = cabinetCenter.z;

    const cabinetProj = cx * wallTangent.x + cz * wallTangent.z;
    const isInsideCabinetSpan = (p) =>
      Math.abs(p.x * wallTangent.x + p.z * wallTangent.z - cabinetProj) < halfW;

    // Cabinet corners: back-left, back-right, front-right, front-left
    const bl = {
      x: cx - wallTangent.x * halfW - wallNormal.x * halfD,
      z: cz - wallTangent.z * halfW - wallNormal.z * halfD,
    };
    const br = {
      x: cx + wallTangent.x * halfW - wallNormal.x * halfD,
      z: cz + wallTangent.z * halfW - wallNormal.z * halfD,
    };
    const fr = {
      x: cx + wallTangent.x * halfW + wallNormal.x * halfD,
      z: cz + wallTangent.z * halfW + wallNormal.z * halfD,
    };
    const fl = {
      x: cx - wallTangent.x * halfW + wallNormal.x * halfD,
      z: cz - wallTangent.z * halfW + wallNormal.z * halfD,
    };

    // Build overhang strip polygons
    const leftStrip = [
      {
        x: bl.x - wallTangent.x * overhang,
        z: bl.z - wallTangent.z * overhang,
      },
      { ...bl },
      { ...fl },
      {
        x: fl.x - wallTangent.x * overhang,
        z: fl.z - wallTangent.z * overhang,
      },
    ];
    const rightStrip = [
      { ...br },
      {
        x: br.x + wallTangent.x * overhang,
        z: br.z + wallTangent.z * overhang,
      },
      {
        x: fr.x + wallTangent.x * overhang,
        z: fr.z + wallTangent.z * overhang,
      },
      { ...fr },
    ];
    // The front strip spans the cabinet face BETWEEN the two side strips —
    // the corners are the side overhangs' business, not the front's. A
    // return wall flush with the end of a vanity otherwise clips the front
    // strip's corner and, because SAT reports any overlap at all, vetoes the
    // front overhang across the whole cabinet: `Scan 1` bathroom B lost its
    // 1" nosing to a 25" return wall reaching 0.2" into the cabinet's span.
    // Inset by `overhang` (not a new constant) so a genuine obstacle in
    // front of the cabinet still suppresses, while a corner touch does not.
    const flInset = {
      x: fl.x + wallTangent.x * overhang,
      z: fl.z + wallTangent.z * overhang,
    };
    const frInset = {
      x: fr.x - wallTangent.x * overhang,
      z: fr.z - wallTangent.z * overhang,
    };
    // Swept over the FRONT figure, not the nosing: a deeper overhang reaches
    // further into the room and must be tested over the ground it actually
    // covers, or it is granted across an obstacle the 1" strip never touched.
    const frontStrip = [
      { ...flInset },
      { ...frInset },
      {
        x: frInset.x + wallNormal.x * front,
        z: frInset.z + wallNormal.z * front,
      },
      {
        x: flInset.x + wallNormal.x * front,
        z: flInset.z + wallNormal.z * front,
      },
    ];

    // Collect obstacle polygons: nearby objects + walls
    const obstacles = [];

    // Objects (showers, toilets, tubs, storage)
    const obstructionTypes = ['shower', 'storage', 'bathtub', 'toilet', 'tub'];
    for (const obj of roomData.objects || []) {
      const objType = (obj.type || '').toLowerCase();
      const categoryKeys = obj.category ? Object.keys(obj.category) : [];
      const isRelevant = obstructionTypes.some(
        (p) =>
          objType.includes(p) ||
          categoryKeys.some((k) => k.toLowerCase().includes(p))
      );
      if (!isRelevant || !obj.center || !obj.dimensions) continue;
      // Same self-exclusion as calculateAvailableWallSpace: a scanned
      // vanity cabinet keeps `category: {storage: {}}`, and its own edge
      // touches every overhang strip — suppressing all three overhangs on
      // every scanned vanity.
      if (isInsideCabinetSpan(obj.center)) continue;

      const r = obj.rotation?.y || 0;
      const cosR = Math.abs(Math.cos(r));
      const sinR = Math.abs(Math.sin(r));
      const ohw =
        ((obj.dimensions.x || 0.5) / 2) * cosR +
        ((obj.dimensions.z || 0.5) / 2) * sinR;
      const ohd =
        ((obj.dimensions.x || 0.5) / 2) * sinR +
        ((obj.dimensions.z || 0.5) / 2) * cosR;
      obstacles.push([
        { x: obj.center.x - ohw, z: obj.center.z - ohd },
        { x: obj.center.x + ohw, z: obj.center.z - ohd },
        { x: obj.center.x + ohw, z: obj.center.z + ohd },
        { x: obj.center.x - ohw, z: obj.center.z + ohd },
      ]);
    }

    // Adjacent walls (not the parent wall, tall enough to matter)
    for (const wall of roomData.walls || []) {
      if (wall.id === parentWall.id) continue;
      if (getWallHeight(wall) < 1.0) continue;
      const wsEndpoints = deriveWallEndpoints(wall);
      const ws = wsEndpoints?.start;
      const we = wsEndpoints?.end;
      if (!ws || !we) continue;
      const wn = wall.normal || { x: 0, z: 0 };
      const wt = 0.05; // thin wall polygon
      obstacles.push([
        { x: ws.x - wn.x * wt, z: ws.z - wn.z * wt },
        { x: we.x - wn.x * wt, z: we.z - wn.z * wt },
        { x: we.x + wn.x * wt, z: we.z + wn.z * wt },
        { x: ws.x + wn.x * wt, z: ws.z + wn.z * wt },
      ]);
    }

    // SAT check each overhang strip against obstacles
    for (const obstacle of obstacles) {
      if (leftOverhang > 0 && doPolygonsOverlap(leftStrip, obstacle)) {
        leftOverhang = 0;
      }
      if (rightOverhang > 0 && doPolygonsOverlap(rightStrip, obstacle)) {
        rightOverhang = 0;
      }
      if (frontOverhang > 0 && doPolygonsOverlap(frontStrip, obstacle)) {
        frontOverhang = 0;
      }
      if (leftOverhang === 0 && rightOverhang === 0 && frontOverhang === 0)
        break;
    }
  }

  // Compute countertop dimensions
  const countertopWidth = cabinetWidth + leftOverhang + rightOverhang;
  const countertopDepth = cabinetDepth + frontOverhang;

  // Compute center offset for asymmetric overhang
  const wallNormal = getWallInwardNormal(parentWall);
  const wallTangent = { x: -wallNormal.z, z: wallNormal.x };

  const lateralShift = (rightOverhang - leftOverhang) / 2;
  const frontShift = frontOverhang / 2;

  const centerOffset = {
    x: lateralShift * wallTangent.x + wallNormal.x * frontShift,
    z: lateralShift * wallTangent.z + wallNormal.z * frontShift,
  };

  return {
    leftOverhang,
    rightOverhang,
    frontOverhang,
    countertopWidth,
    countertopDepth,
    centerOffset,
  };
}

module.exports = {
  squareToWall,
  quarterTurnFrom,
  wallFacingExtent,
  // Constants
  MIRROR_MOUNTING_GAP,

  // Geometry primitives
  distancePointToSegment,
  raySegmentIntersection2D,
  isPointInPolygon,
  pointInsidePolygon,
  lineSegmentsIntersect,
  lineSegmentIntersectsPolygon,

  // Room/Wall helpers
  calculatePolygonCentroid,
  getFloorY,
  getRoomType,
  getRoomCenter,
  getWallCenter,
  getWallHeight,
  getObjectHeight,
  getWallInwardNormal,
  makeWallProjector,
  deriveWallEndpoints,
  clampLateralToWallExtent,
  getWallFacingRotation,
  getWallSnappedPosition,
  getWallPositionedSnap,
  reSnapToWallPreservingLateralPosition,
  findWallByDirection,

  // Vanity-specific placement
  resolveVanityPlacement,
  calculateAvailableWallSpace,
  calculateMaxVanityDepth,
  calculateFrontClearance,
  checkVanityObjectCollision,
  computeCountertopOverhangs,

  // Fixture placement
  resolveFixturePlacement,
};
